import React, { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api, { API_BASE } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import {
    ArrowUpRight,
    QrCode,
    AlertCircle,
    Receipt,
    Users,
    Megaphone,
    Car,
    Bike,
    Sparkles,
    KeyRound,
    AlertTriangle,
    Camera,
    X,
    Building2,
    ShieldCheck,
} from "lucide-react";
import VisitorPhotoModal, { getVisitorPhotoSrc } from "@/components/VisitorPhotoModal";
import ResidentRecentActivity from "@/components/ResidentRecentActivity";
import { toast } from "sonner";

function Stat({ label, value, hint, icon: Icon, testid }) {
    return (
        <div className="nest-card p-4 sm:p-5 flex flex-col justify-between hover:border-nest-clay transition-all duration-200" data-testid={testid}>
            <div className="flex items-center justify-between gap-2">
                <p className="nest-stat-label text-[10px] sm:text-[11px] truncate">{label}</p>
                {Icon && <Icon size={15} className="text-nest-ink3 shrink-0" />}
            </div>
            <p className="font-display text-2xl sm:text-3xl lg:text-4xl mt-2 tracking-tight text-nest-ink font-bold">{value}</p>
            {hint && <p className="text-[11px] text-nest-ink3 mt-1 truncate">{hint}</p>}
        </div>
    );
}

function formatTimeSafe(dateStr) {
    if (!dateStr) return "";
    try {
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return "";
        return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    } catch {
        return "";
    }
}

function ResidentDash({ user, stats }) {
    const navigate = useNavigate();
    const [pending, setPending] = useState([]);
    const [parkingAlerts, setParkingAlerts] = useState([]);
    const [alertDismissed, setAlertDismissed] = useState(false);
    const [activePhotoVisitor, setActivePhotoVisitor] = useState(null);

    const loadPending = () => {
        api.get("/walkins/pending/me")
            .then((r) => setPending(r.data || []))
            .catch(() => {});
        api.get("/resident/parking-alerts")
            .then((r) => setParkingAlerts(r.data || []))
            .catch(() => {});
    };

    useEffect(() => {
        loadPending();
        const iv = setInterval(() => {
            if (typeof document !== "undefined" && document.visibilityState === "visible") {
                loadPending();
            }
        }, 6000);
        return () => clearInterval(iv);
    }, []);

    const decide = async (w, decision) => {
        try {
            await api.post(`/walkins/${w.walkin_id}/decision`, { decision });
            setPending((arr) => arr.filter((x) => x.walkin_id !== w.walkin_id));
            toast.success(decision === "approved" ? `✓ Approved entry for ${w.name}` : `✕ Denied entry for ${w.name}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Action failed");
        }
    };

    const getGreeting = () => {
        const hour = new Date().getHours();
        if (hour < 12) return "Good morning";
        if (hour < 17) return "Good afternoon";
        return "Good evening";
    };

    const firstName = user?.name ? user.name.split(" ")[0] : "Resident";
    const residentAddress = user?.tower && user?.flat_no ? `Wing ${user.tower} · Flat ${user.flat_no}` : (user?.flat_no || "Resident");

    return (
        <div className="space-y-4 sm:space-y-6 animate-fade-up">
            {/* 1. Parking Alerts (Compact Strip Banner) */}
            {parkingAlerts.length > 0 && !alertDismissed && (
                <div data-testid="parking-alerts-compact-banner">
                    <div className="rounded-xl border border-rose-300 bg-rose-50/90 px-3.5 py-2.5 flex items-center justify-between gap-3 shadow-2xs">
                        <div className="flex items-center gap-2.5 min-w-0">
                            <AlertTriangle size={17} className="text-rose-600 shrink-0 animate-pulse" />
                            <p className="text-xs font-semibold text-rose-950 truncate">
                                <span className="font-bold text-rose-700">Wing {user?.tower || "C"} Parking Alert:</span>{" "}
                                {parkingAlerts.length === 1
                                    ? `Unauthorized vehicle ${parkingAlerts[0].vehicle_no} reported.`
                                    : `${parkingAlerts.length} unauthorized vehicles reported.`}
                            </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            <button
                                type="button"
                                onClick={() => navigate("/notices")}
                                className="text-xs font-bold text-rose-700 hover:text-rose-900 underline cursor-pointer"
                            >
                                View Notice
                            </button>
                            <button
                                type="button"
                                onClick={() => setAlertDismissed(true)}
                                className="p-1 rounded-lg text-rose-500 hover:text-rose-700 hover:bg-rose-100/60 cursor-pointer"
                                aria-label="Dismiss"
                            >
                                <X size={15} />
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 2. Priority Visitor Approval Hero Card (The "Arrival Call") */}
            {pending.length > 0 && (
                <div className="rounded-2xl border-2 border-amber-400 bg-gradient-to-br from-amber-50/90 via-white to-amber-50/40 p-4 sm:p-6 shadow-md" data-testid="pending-walkins-banner">
                    <div className="flex items-center justify-between gap-3 pb-3 border-b border-amber-200/70">
                        <div className="flex items-center gap-2.5">
                            <span className="relative flex h-3 w-3">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-3 w-3 bg-amber-500"></span>
                            </span>
                            <div>
                                <span className="text-[10px] font-black uppercase tracking-widest text-amber-800">Gate Arrival</span>
                                <h3 className="font-display text-lg sm:text-2xl font-bold tracking-tight text-amber-950">
                                    {pending.length} visitor{pending.length > 1 ? "s" : ""} awaiting your approval
                                </h3>
                            </div>
                        </div>
                        <span className="text-xs font-mono font-bold bg-amber-100 text-amber-900 px-2.5 py-1 rounded-full border border-amber-300">
                            Live At Gate
                        </span>
                    </div>

                    <div className="mt-3.5 grid gap-3">
                        {pending.map((w) => (
                            <div
                                key={w.walkin_id}
                                className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3.5 bg-white border border-amber-200 rounded-xl hover:shadow-xs transition"
                                data-testid={`pending-walkin-${w.walkin_id}`}
                            >
                                <div className="flex items-center gap-3.5 min-w-0">
                                    <div className="relative shrink-0">
                                        <img
                                            src={getVisitorPhotoSrc(w)}
                                            alt=""
                                            onClick={() => setActivePhotoVisitor(w)}
                                            className="h-16 w-16 rounded-xl object-cover bg-nest-alt cursor-pointer hover:ring-2 hover:ring-amber-500 transition shadow-2xs"
                                            title="Click to view full photo"
                                        />
                                        <span className="absolute -bottom-1.5 -right-1.5 p-1 rounded-md bg-white border border-nest-line shadow-2xs">
                                            <Camera size={11} className="text-nest-ink" />
                                        </span>
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <p className="font-bold text-base text-nest-ink truncate">{w.name}</p>
                                            <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 border border-amber-200">
                                                {w.purpose || "Visitor"}
                                            </span>
                                            {w.vehicle_no && (
                                                <span className="inline-flex items-center gap-1 font-mono text-[11px] bg-nest-alt px-2 py-0.5 rounded border border-nest-line text-nest-ink font-semibold">
                                                    {w.vehicle_type === "bike" ? <Bike size={12} className="text-nest-clay" /> : <Car size={12} className="text-nest-clay" />}
                                                    <span>{w.vehicle_no}</span>
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-xs text-nest-ink2 mt-1">
                                            {w.phone ? `📞 ${w.phone} · ` : ""}{w.created_at ? `Arrived at ${formatTimeSafe(w.created_at)}` : ""}
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-2 w-full sm:w-auto shrink-0 pt-2 sm:pt-0 border-t border-nest-line/50 sm:border-0">
                                    <button
                                        onClick={() => navigate(`/walkin/${w.walkin_id}`)}
                                        className="flex-1 sm:flex-none text-xs font-semibold px-3 py-2.5 rounded-xl border border-nest-line hover:border-nest-ink text-nest-ink transition active:scale-95"
                                        data-testid={`walkin-view-${w.walkin_id}`}
                                    >
                                        Details
                                    </button>
                                    <button
                                        onClick={() => decide(w, "approved")}
                                        className="flex-1 sm:flex-none text-xs font-bold px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white shadow-xs transition active:scale-95 flex items-center justify-center gap-1.5"
                                        data-testid={`walkin-quickapprove-${w.walkin_id}`}
                                    >
                                        <span>✓ Approve Entry</span>
                                    </button>
                                    <button
                                        onClick={() => decide(w, "rejected")}
                                        className="flex-1 sm:flex-none text-xs font-bold px-3.5 py-2.5 rounded-xl border border-rose-300 text-rose-700 hover:bg-rose-50 transition active:scale-95"
                                        data-testid={`walkin-quickreject-${w.walkin_id}`}
                                    >
                                        <span>✕ Deny</span>
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <VisitorPhotoModal visitor={activePhotoVisitor} onClose={() => setActivePhotoVisitor(null)} />

            {/* 3. Resident Hero Welcome & Quick Status */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6">
                <div className="lg:col-span-7 xl:col-span-8 nest-card p-6 sm:p-8 flex flex-col justify-between relative overflow-hidden bg-gradient-to-br from-white via-white to-nest-base/50">
                    <div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider bg-nest-alt text-nest-ink px-2.5 py-1 rounded-full border border-nest-line">
                                <Building2 size={12} className="text-nest-clay" />
                                <span>{residentAddress}</span>
                            </span>
                            <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500"></span>
                                <span>Gate System Online</span>
                            </span>
                        </div>

                        <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight text-nest-ink mt-3 sm:mt-4 font-bold">
                            {getGreeting()}, {firstName}.
                        </h1>
                        <p className="text-nest-ink2 text-xs sm:text-sm lg:text-base mt-2 max-w-xl leading-relaxed">
                            Pre-approve guests, check security logs, or clear society dues — everything you need for your flat is right here.
                        </p>
                    </div>

                    <div className="flex flex-col sm:flex-row flex-wrap gap-2.5 sm:gap-3 mt-6 pt-5 border-t border-nest-line/60">
                        <button
                            onClick={() => navigate("/visitors")}
                            className="nest-btn-primary flex items-center justify-center gap-2 text-sm shadow-xs active:scale-95 cursor-pointer"
                            data-testid="cta-new-visitor"
                        >
                            <QrCode size={16} />
                            <span>Create Visitor Pass</span>
                        </button>
                        <button
                            onClick={() => navigate("/complaints")}
                            className="px-4 py-2.5 rounded-xl border border-nest-line hover:border-nest-ink font-semibold text-sm text-nest-ink text-center transition active:scale-95 cursor-pointer"
                            data-testid="cta-raise-complaint"
                        >
                            <span>Raise a Complaint</span>
                        </button>
                    </div>
                </div>

                {/* Society At A Glance Stats Grid */}
                <div className="lg:col-span-5 xl:col-span-4 grid grid-cols-2 gap-2.5 sm:gap-3.5">
                    <Stat label="Notices" value={stats?.notices_count ?? 0} hint="From admin" icon={Megaphone} testid="stat-notices" />
                    <Stat label="Open complaints" value={stats?.open_complaints ?? 0} hint="Society-wide" icon={AlertCircle} testid="stat-open-complaints" />
                    <Stat label="Residents" value={stats?.total_residents ?? 0} hint="In directory" icon={Users} testid="stat-residents" />
                    <Stat label="Visitors today" value={stats?.visitors_today ?? 0} hint="At society gate" icon={ShieldCheck} testid="stat-visitors-today" />
                </div>
            </div>

            {/* 4. Quick Paths (Bento Grid) */}
            <div className="nest-card p-5 sm:p-7">
                <div className="flex items-center justify-between pb-3.5 border-b border-nest-line/60">
                    <div>
                        <p className="nest-stat-label">Quick Paths</p>
                        <h3 className="font-display text-lg sm:text-xl font-bold tracking-tight text-nest-ink mt-0.5">
                            Frequent Services
                        </h3>
                    </div>
                </div>

                <div className="mt-4 grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                    {[
                        { to: "/visitors", label: "Visitor Passes", desc: "Guest QR & pre-approvals", icon: QrCode, testid: "quick-visitors" },
                        { to: "/bills", label: "Maintenance Bills", desc: "Pay dues & download receipts", icon: Receipt, testid: "quick-bills" },
                        { to: "/notices", label: "Society Notices", desc: "Alerts & announcements", icon: Megaphone, testid: "quick-notices" },
                        { to: "/directory", label: "Neighbour Directory", desc: "Wings, flats & committee", icon: Users, testid: "quick-directory" },
                    ].map((q) => (
                        <button
                            key={q.to}
                            onClick={() => navigate(q.to)}
                            className="text-left p-4 sm:p-5 rounded-xl border border-nest-line hover:border-nest-clay hover:bg-white bg-nest-base/30 transition-all duration-200 group active:scale-95 cursor-pointer flex flex-col justify-between"
                            data-testid={q.testid}
                        >
                            <div>
                                <div className="p-2.5 rounded-lg bg-white border border-nest-line w-fit text-nest-clay group-hover:bg-nest-clay group-hover:text-white transition-colors shadow-2xs">
                                    <q.icon size={20} />
                                </div>
                                <p className="mt-3 font-display font-bold text-base text-nest-ink leading-snug">{q.label}</p>
                                <p className="text-xs text-nest-ink3 mt-0.5 hidden sm:block">{q.desc}</p>
                            </div>
                            <div className="flex items-center justify-between mt-3 text-xs font-semibold text-nest-ink3 group-hover:text-nest-clay transition-colors">
                                <span>Open</span>
                                <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                            </div>
                        </button>
                    ))}
                </div>
            </div>

            {/* 5. Live Resident Gate Activity Feed */}
            <div>
                <ResidentRecentActivity user={user} onPhotoClick={setActivePhotoVisitor} />
            </div>
        </div>
    );
}

function AdminDash({ user, stats }) {
    const navigate = useNavigate();
    const pendingCount = stats?.pending_residents ?? 0;
    const isSuperAdmin = (user?.email || "").toLowerCase() === "aws.tilak007@gmail.com";

    return (
        <div className="space-y-6 sm:space-y-8 animate-fade-up">
            {/* 1. Header Banner & Quick Controls */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 pb-4 border-b border-nest-line/70">
                <div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider bg-nest-ink text-white px-2.5 py-0.5 rounded-full">
                            Admin Control Room
                        </span>
                        <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-emerald-700 bg-emerald-50 px-2.5 py-0.5 rounded-full border border-emerald-200">
                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                            <span>Live System Online</span>
                        </span>
                    </div>
                    <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight text-nest-ink font-bold mt-2">
                        Society Operations Hub
                    </h1>
                    <p className="text-nest-ink2 text-xs sm:text-sm mt-1 max-w-xl">
                        Real-time overview of gate security, maintenance collections, resident affairs, and pending tasks.
                    </p>
                </div>

                <div className="flex items-center gap-2.5 flex-wrap">
                    {isSuperAdmin && (
                        <button
                            onClick={() => navigate("/directory?tab=test-admins")}
                            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-900 text-xs font-bold transition cursor-pointer shadow-2xs active:scale-95"
                            title="Manage Test Admin accounts (Super Admin only)"
                            data-testid="dashboard-manage-test-admins-btn"
                        >
                            <KeyRound size={14} className="text-amber-700" />
                            <span>Manage Test Admins</span>
                        </button>
                    )}
                    <button
                        onClick={() => navigate("/notices")}
                        className="nest-btn-primary flex items-center gap-1.5 text-xs sm:text-sm py-2 px-3.5 active:scale-95 cursor-pointer shadow-xs"
                    >
                        <Megaphone size={14} />
                        <span>Post Notice</span>
                    </button>
                    <button
                        onClick={() => navigate("/bills")}
                        className="px-3.5 py-2 rounded-xl border border-nest-line hover:border-nest-ink font-semibold text-xs sm:text-sm text-nest-ink transition active:scale-95 cursor-pointer bg-white"
                    >
                        <span>Generate Bills</span>
                    </button>
                </div>
            </div>

            {/* 2. Priority Pending Residents Verification Alert */}
            {pendingCount > 0 && (
                <div className="p-4 sm:p-5 rounded-2xl border-2 border-amber-400 bg-gradient-to-r from-amber-50/95 via-amber-50/70 to-white shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 animate-fade-in" data-testid="pending-residents-banner">
                    <div className="flex items-center gap-3">
                        <span className="relative flex h-3.5 w-3.5 shrink-0">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-amber-500"></span>
                        </span>
                        <div>
                            <p className="font-bold text-amber-950 text-sm sm:text-base">
                                {pendingCount} Resident Profile{pendingCount > 1 ? "s" : ""} Awaiting Verification
                            </p>
                            <p className="text-xs text-amber-800 mt-0.5">
                                Verify flat numbers and ownership to grant access to society notices and gate approvals.
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={() => navigate("/directory")}
                        className="px-4 py-2.5 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-bold text-xs sm:text-sm whitespace-nowrap transition cursor-pointer self-start sm:self-auto w-full sm:w-auto text-center shadow-xs active:scale-95"
                    >
                        Review Profiles →
                    </button>
                </div>
            )}

            {/* 3. Core Society Operational KPI Bento Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-5">
                <div className="nest-card p-4 sm:p-5 flex flex-col justify-between hover:border-nest-clay transition-all group" data-testid="stat-visitors-today">
                    <div className="flex items-center justify-between gap-2">
                        <p className="nest-stat-label text-[10px] sm:text-[11px]">Gate Traffic</p>
                        <div className="p-1.5 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200">
                            <ShieldCheck size={16} />
                        </div>
                    </div>
                    <p className="font-display text-2xl sm:text-3xl lg:text-4xl mt-2 tracking-tight text-nest-ink font-bold">
                        {stats?.visitors_today ?? 0}
                    </p>
                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-nest-line/50 text-[11px] text-nest-ink3">
                        <span>Visitors today</span>
                        <span className="text-emerald-700 font-bold">Live at Gate</span>
                    </div>
                </div>

                <div className="nest-card p-4 sm:p-5 flex flex-col justify-between hover:border-nest-clay transition-all group" data-testid="stat-unpaid-bills">
                    <div className="flex items-center justify-between gap-2">
                        <p className="nest-stat-label text-[10px] sm:text-[11px]">Maintenance</p>
                        <div className="p-1.5 rounded-lg bg-amber-50 text-amber-700 border border-amber-200">
                            <Receipt size={16} />
                        </div>
                    </div>
                    <p className="font-display text-2xl sm:text-3xl lg:text-4xl mt-2 tracking-tight text-amber-900 font-bold">
                        {stats?.unpaid_bills ?? 0}
                    </p>
                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-nest-line/50 text-[11px] text-nest-ink3">
                        <span>Unpaid bills</span>
                        <span className="text-amber-700 font-bold">Pending Dues</span>
                    </div>
                </div>

                <div className="nest-card p-4 sm:p-5 flex flex-col justify-between hover:border-nest-clay transition-all group" data-testid="stat-open-complaints">
                    <div className="flex items-center justify-between gap-2">
                        <p className="nest-stat-label text-[10px] sm:text-[11px]">Helpdesk</p>
                        <div className="p-1.5 rounded-lg bg-rose-50 text-rose-700 border border-rose-200">
                            <AlertCircle size={16} />
                        </div>
                    </div>
                    <p className="font-display text-2xl sm:text-3xl lg:text-4xl mt-2 tracking-tight text-nest-ink font-bold">
                        {stats?.open_complaints ?? 0}
                    </p>
                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-nest-line/50 text-[11px] text-nest-ink3">
                        <span>Open complaints</span>
                        <span className="text-rose-700 font-bold">Needs Action</span>
                    </div>
                </div>

                <div className="nest-card p-4 sm:p-5 flex flex-col justify-between hover:border-nest-clay transition-all group" data-testid="stat-residents">
                    <div className="flex items-center justify-between gap-2">
                        <p className="nest-stat-label text-[10px] sm:text-[11px]">Community</p>
                        <div className="p-1.5 rounded-lg bg-blue-50 text-blue-700 border border-blue-200">
                            <Users size={16} />
                        </div>
                    </div>
                    <p className="font-display text-2xl sm:text-3xl lg:text-4xl mt-2 tracking-tight text-nest-ink font-bold">
                        {stats?.total_residents ?? 0}
                    </p>
                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-nest-line/50 text-[11px] text-nest-ink3">
                        <span>Verified residents</span>
                        <span className="text-blue-700 font-bold">Directory</span>
                    </div>
                </div>
            </div>

            {/* 4. Society Operational Modules Bento Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                <a
                    href="/notices"
                    className="nest-card p-5 sm:p-6 block hover:border-nest-clay hover:shadow-md transition-all group flex flex-col justify-between bg-gradient-to-br from-white via-white to-nest-base/30"
                    data-testid="admin-card-notices"
                >
                    <div>
                        <div className="flex items-center justify-between w-full">
                            <div className="p-3 rounded-2xl bg-amber-50 text-amber-700 border border-amber-200 group-hover:bg-amber-600 group-hover:text-white transition-colors">
                                <Megaphone size={22} />
                            </div>
                            <span className="text-xs font-bold text-nest-ink3 group-hover:text-nest-clay transition-colors flex items-center gap-1">
                                <span>Publish</span>
                                <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                            </span>
                        </div>
                        <h3 className="font-display text-xl sm:text-2xl mt-4 font-bold tracking-tight text-nest-ink">
                            Post a Notice
                        </h3>
                        <p className="text-xs sm:text-sm text-nest-ink2 mt-1.5 leading-relaxed">
                            Broadcast circulars, maintenance schedules, and urgent alerts to all residents or specific wings.
                        </p>
                    </div>
                    <div className="mt-4 pt-3 border-t border-nest-line/50 flex items-center justify-between text-xs font-semibold text-nest-ink3">
                        <span>{stats?.notices_count ?? 0} published notices</span>
                        <span className="text-nest-clay underline">Open Noticeboard</span>
                    </div>
                </a>

                <a
                    href="/bills"
                    className="nest-card p-5 sm:p-6 block hover:border-nest-clay hover:shadow-md transition-all group flex flex-col justify-between bg-gradient-to-br from-white via-white to-nest-base/30"
                    data-testid="admin-card-bills"
                >
                    <div>
                        <div className="flex items-center justify-between w-full">
                            <div className="p-3 rounded-2xl bg-emerald-50 text-emerald-700 border border-emerald-200 group-hover:bg-emerald-600 group-hover:text-white transition-colors">
                                <Receipt size={22} />
                            </div>
                            <span className="text-xs font-bold text-nest-ink3 group-hover:text-nest-clay transition-colors flex items-center gap-1">
                                <span>Manage</span>
                                <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                            </span>
                        </div>
                        <h3 className="font-display text-xl sm:text-2xl mt-4 font-bold tracking-tight text-nest-ink">
                            Generate Bills
                        </h3>
                        <p className="text-xs sm:text-sm text-nest-ink2 mt-1.5 leading-relaxed">
                            Track monthly maintenance charges, monitor overdue flats, and record cash/UPI payments.
                        </p>
                    </div>
                    <div className="mt-4 pt-3 border-t border-nest-line/50 flex items-center justify-between text-xs font-semibold text-nest-ink3">
                        <span>{stats?.unpaid_bills ?? 0} flats with pending dues</span>
                        <span className="text-nest-clay underline">Open Billing</span>
                    </div>
                </a>

                <a
                    href="/complaints"
                    className="nest-card p-5 sm:p-6 block hover:border-nest-clay hover:shadow-md transition-all group flex flex-col justify-between bg-gradient-to-br from-white via-white to-nest-base/30"
                    data-testid="admin-card-complaints"
                >
                    <div>
                        <div className="flex items-center justify-between w-full">
                            <div className="p-3 rounded-2xl bg-rose-50 text-rose-700 border border-rose-200 group-hover:bg-rose-600 group-hover:text-white transition-colors">
                                <AlertCircle size={22} />
                            </div>
                            <span className="text-xs font-bold text-nest-ink3 group-hover:text-nest-clay transition-colors flex items-center gap-1">
                                <span>Resolve</span>
                                <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                            </span>
                        </div>
                        <h3 className="font-display text-xl sm:text-2xl mt-4 font-bold tracking-tight text-nest-ink">
                            Resolve Complaints
                        </h3>
                        <p className="text-xs sm:text-sm text-nest-ink2 mt-1.5 leading-relaxed">
                            Track resident grievance tickets (plumbing, lifts, noise, parking) and update their progress.
                        </p>
                    </div>
                    <div className="mt-4 pt-3 border-t border-nest-line/50 flex items-center justify-between text-xs font-semibold text-nest-ink3">
                        <span>{stats?.open_complaints ?? 0} open tickets</span>
                        <span className="text-nest-clay underline">Open Helpdesk</span>
                    </div>
                </a>
            </div>

            {/* 5. Quick Administrative Shortcuts Bar */}
            <div className="nest-card p-5 sm:p-6 flex flex-col sm:flex-row items-center justify-between gap-4 bg-nest-alt/30">
                <div className="flex items-center gap-3 text-center sm:text-left">
                    <div className="p-2.5 rounded-xl bg-nest-ink text-white shrink-0">
                        <Building2 size={20} />
                    </div>
                    <div>
                        <h4 className="font-bold text-sm sm:text-base text-nest-ink">Need Gate or Staff Controls?</h4>
                        <p className="text-xs text-nest-ink2 mt-0.5">Switch directly to Security Gate desk or inspect society directory.</p>
                    </div>
                </div>
                <div className="flex items-center gap-2.5 w-full sm:w-auto">
                    <button
                        onClick={() => navigate("/guard")}
                        className="flex-1 sm:flex-none text-xs font-bold px-4 py-2.5 rounded-xl bg-nest-ink text-white hover:bg-neutral-800 transition active:scale-95 shadow-xs"
                    >
                        Security Gate Console →
                    </button>
                    <button
                        onClick={() => navigate("/directory")}
                        className="flex-1 sm:flex-none text-xs font-bold px-4 py-2.5 rounded-xl border border-nest-line hover:border-nest-ink bg-white text-nest-ink transition active:scale-95"
                    >
                        Resident Directory
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function Dashboard() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);

    useEffect(() => {
        if (user?.role === "guard") {
            navigate("/guard", { replace: true });
            return;
        }
        api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    }, [user, navigate]);

    if (!user) return null;

    return (
        <div className="animate-fade-up" data-testid="dashboard-root">
            {user.role === "admin" ? <AdminDash user={user} stats={stats} /> : <ResidentDash user={user} stats={stats} />}
        </div>
    );
}
